import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { communitiesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Users, Globe, Calendar, ExternalLink } from 'lucide-react';

export const CommunityDetailPage = () => {
  const { id } = useParams();
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCommunity = async () => {
      try {
        const res = await communitiesAPI.getById(id);
        setCommunity(res.data);
      } catch (err) {
        console.error('Error fetching community:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCommunity();
  }, [id]);

  if (loading) {
    return (
      <div className="container-wide py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-12 bg-muted rounded w-1/2" />
          <div className="h-4 bg-muted rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!community) {
    return (
      <div className="container-wide py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Community not found</h1>
        <Link to="/communities">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Communities
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="community-detail-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <Link to="/communities" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Communities
          </Link>
          
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-sm bg-muted flex items-center justify-center">
              {community.image_url ? (
                <img src={community.image_url} alt={community.name} className="w-full h-full object-cover rounded-sm" />
              ) : (
                <Users className="w-10 h-10 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <span className="badge-category mb-2 inline-block">{community.category}</span>
              <h1 className="text-display text-3xl lg:text-4xl mb-3">{community.name}</h1>
              <p className="text-muted-foreground max-w-2xl">{community.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section>
        <div className="container-wide py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              <div>
                <h2 className="text-heading text-xl mb-4">About this Community</h2>
                <p className="text-muted-foreground leading-relaxed">{community.description}</p>
              </div>

              {/* Tags */}
              {community.tags && community.tags.length > 0 && (
                <div>
                  <h2 className="text-heading text-xl mb-4">Topics</h2>
                  <div className="flex flex-wrap gap-2">
                    {community.tags.map((tag, i) => (
                      <span key={i} className="badge-category">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6 space-y-4">
                  {community.member_count && (
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{community.member_count.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">Members</div>
                      </div>
                    </div>
                  )}
                  {community.meetup_frequency && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{community.meetup_frequency}</div>
                        <div className="text-xs text-muted-foreground">Meetup Schedule</div>
                      </div>
                    </div>
                  )}
                  {community.website && (
                    <a
                      href={community.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-primary hover:underline"
                    >
                      <Globe className="w-5 h-5" />
                      <span>Visit Website</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </CardContent>
              </Card>

              <Button className="w-full" size="lg">
                Join Community
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
